"use client";

import { useCallback, useState } from "react";
import { isNativeMarketoForm } from "@marketing-sites/drlp";

import { NativeMarketoForm } from "./native-marketo-form";
import { FallbackForm } from "./fallback-form";

export interface DrlpHeroFormProps {
  /** Marketo form id selected in Sanity. */
  marketoFormId?: string;
  /** Marketo instance base URL — required for native forms. */
  marketoBaseUrl?: string;
  /** Marketo Munchkin id — required for native forms. */
  marketoMunchkinId?: string;
  formPostSubmitAction?: "showMessage" | "redirect" | "downloadPdf";
  formCTAText?: string;
  formSuccessTitleText?: string;
  formSuccessBodyText?: string;
  formSuccessRedirectUrl?: string;
  pdfUrl?: string;
  pdfFilename?: string;
}

/**
 * Renders the DRLP hero form.
 *
 * - Progressive Profiling (2601) / Goldcast Webinar (2803): mounts the native Marketo form so Marketo's
 *   runtime decides which fields to show per visitor. If `forms2.min.js` fails
 *   to load (or the instance config is missing), falls back to the Content
 *   Registration (1021) form.
 * - Content Registration (1021) or View Demos (2377): renders the matching
 *   fallback form directly.
 */
export function DrlpHeroForm({
  marketoFormId,
  marketoBaseUrl,
  marketoMunchkinId,
  formPostSubmitAction,
  formCTAText,
  formSuccessTitleText,
  formSuccessBodyText,
  formSuccessRedirectUrl,
  pdfUrl,
  pdfFilename,
}: DrlpHeroFormProps) {
  const [nativeFailed, setNativeFailed] = useState(false);
  const handleLoadError = useCallback(() => setNativeFailed(true), []);

  const isNative =
    isNativeMarketoForm(marketoFormId) &&
    !!marketoBaseUrl &&
    !!marketoMunchkinId;

  if (isNative && marketoFormId && !nativeFailed) {
    return (
      <NativeMarketoForm
        baseUrl={marketoBaseUrl}
        munchkinId={marketoMunchkinId}
        formId={marketoFormId}
        formPostSubmitAction={formPostSubmitAction}
        successTitleText={formSuccessTitleText}
        successBodyText={formSuccessBodyText}
        successRedirectUrl={formSuccessRedirectUrl}
        pdfUrl={pdfUrl}
        pdfFilename={pdfFilename}
        onLoadError={handleLoadError}
      />
    );
  }

  return (
    <FallbackForm
      marketoFormId={marketoFormId}
      formPostSubmitAction={formPostSubmitAction}
      ctaText={formCTAText}
      successTitleText={formSuccessTitleText}
      successBodyText={formSuccessBodyText}
      successRedirectUrl={formSuccessRedirectUrl}
      pdfUrl={pdfUrl}
      pdfFilename={pdfFilename}
    />
  );
}
